import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { BusinessService } from '@app/business/business.service';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { managementRequestsI, PaginationParams, SortingParams } from '../hardware-certificates.interface';
import { SessionStorageService } from '@app/core';


@Component({
  selector: 'cloud-business-management-requests-report-qes',
  templateUrl: './business-management-requests-report-qes.component.html',
  styleUrls: ['./business-management-requests-report-qes.component.scss']
})
export class BusinessManagementRequestsReportQESComponent {
  searchForm: FormGroup;
  certificateTable: boolean = false;
  businessAccountId: any;

  showTable: boolean = false;
  enableExport: boolean = false;
  totalCount = 0;
  emptyMassage: string = '';

  managementRequests: managementRequestsI[] = [];
  loading: boolean = false;

  currentFilters: any;
  currentPagination: PaginationParams = { pageIndex: 0, pageSize: 10 };
  currentSorting: SortingParams = { active: '', direction: '' };

  searchCriteria: any

  get fromDateMax(): Date | null {
    return this.searchForm.get('toDate')?.value;
  }

  get toDateMin(): Date | null {
    return this.searchForm.get('fromDate')?.value;
  }

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    public businessService: BusinessService,
    private sessionStorageService: SessionStorageService,
    public info: InfoService,
  ) { }

  ngOnInit(): void {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
    this.buildForm()
  }

  buildForm() {
    this.searchForm = this.formBuilder.group({
      creatorName: new FormControl(''),
      fromDate: new FormControl(''),
      toDate: new FormControl
    });
  }
  clearField(field: string) {
    if (this.searchForm.get(field)) {
      this.searchForm.get(field)?.setValue(null);
    }
  }
  readOnlyValidator(control: AbstractControl): ValidationErrors | null {
    return control.value ? null : { readOnly: true };
  }
  resetFilters() {
    this.searchForm.reset();
    this.searchForm.patchValue({
      creatorName: '',
      fromDate: '',
      toDate: '',
    });

    this.showTable = false;
    this.managementRequests = [];
    this.currentPagination = { pageIndex: 0, pageSize: 10 }
  }

  exportExcel() {
    if (!this.hasData()) {
      this.info.error(this.translateService.instant('certificates.noDataExport'));
      return;
    } else {
      const datePipe = new DatePipe('en-US');
      const formValues = this.searchForm.value;
      let searchCriteria: any = {};
      const allowedKeys = ['creatorName', 'fromDate', 'toDate'];

      Object.keys(formValues).forEach((key) => {
        // Prevent prototype pollution by validating property names
        if (allowedKeys.includes(key) && Object.prototype.hasOwnProperty.call(formValues, key) && formValues[key]) {
          if (['fromDate', 'toDate'].includes(key)) {
            searchCriteria[key] = datePipe.transform(formValues[key], 'dd.MM.yyyy');
          } else {
            searchCriteria[key] = formValues[key];
          }
        }
      });
      this.businessService.getManagementRequestsExcelFormat(searchCriteria).subscribe((response) => {
        let fileData: any;
        let fileName: string;

        if (response.body && typeof response.body === 'object' && response.body.fileContent) {
          fileData = response.body;
          fileName = fileData.filename || 'Managment Report.xlsx';

          const base64Data = fileData.fileContent;
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          this.downloadFile(blob, fileName);

        } else {
          const contentDisposition = response.headers.get('Content-Disposition');
          const matches = /filename="?([^";]+)"?/i.exec(contentDisposition);
          fileName = (matches && matches[1]) ? matches[1].trim() : 'Portal Report.xlsx';

          const blob = new Blob([response.body], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          this.downloadFile(blob, fileName);
        }

        this.info.success(this.translateService.instant('certificates.exportSuccess'));
      });
    }
  }
  private downloadFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  hasData(): boolean {
    return this.managementRequests && Array.isArray(this.managementRequests) && this.managementRequests.length > 0;
  }

  onFormSubmit() {
    this.currentPagination = { ...this.currentPagination, pageIndex: 0 }
    this.loadCertificates();
  }

  loadCertificates() {
    if (!this.searchForm.valid) {
      return;
    }
    this.loading = true;
    const datePipe = new DatePipe('en-US');
    const formValues = { ...this.searchForm.value };
    const allowedKeys = ['creatorName', 'fromDate', 'toDate'];

    delete formValues.vat;

    Object.keys(formValues).forEach((key) => {
      // Prevent prototype pollution by validating property names
      if (allowedKeys.includes(key) && Object.prototype.hasOwnProperty.call(formValues, key) && formValues[key]) {
        if (['fromDate', 'toDate'].includes(key)) {
          formValues[key] = datePipe.transform(formValues[key], 'dd.MM.yyyy');
        } else {
          formValues[key] = formValues[key];
        }
      }
    });
    formValues.hasPaging = true;
    
    let companyCmrId = this.sessionStorageService.getItem("businessAccountId");
    formValues.companyCmrId = companyCmrId;

    const params = this.buildHttpParams(formValues, this.currentPagination, this.currentSorting);
  
    this.businessService.searchForManagementRequests(params).subscribe({
      next: (res) => {
        this.loading = false;
        if (Array.isArray(res.body?.data) && res.body.data.length > 0) {
          this.showTable = true;
          this.managementRequests = res.body.data;
          this.totalCount = res.body.size;
          console.log(' this.totalCount', this.totalCount)
          this.emptyMassage = '';
        } else {
          this.showTable = false;
          this.managementRequests = [];
          this.emptyMassage = this.translateService.instant('certificates.emptyMassage');
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Error loading certificates:', err);
        this.showTable = false;
      }
    })

  }

  onPageChange(event: PaginationParams) {
    this.currentPagination = { ...event };
    this.loadCertificates();
  }

  onSortChange(event: SortingParams) {
    this.currentSorting = { ...event };
    this.currentPagination.pageIndex = 0;
    this.loadCertificates();
  }
  private buildHttpParams(filters: any, pagination: PaginationParams, sorting?: SortingParams): HttpParams {
    let params = new HttpParams();
    params = params.set('offset', (pagination.pageIndex).toString());
    params = params.set('limit', pagination.pageSize.toString());
    if (sorting && sorting.active && sorting.direction) {
      params = params.set('orderByFilter', sorting.active);
      params = params.set('order', sorting.direction.toUpperCase());
    }

    if (filters) {
      Object.keys(filters).forEach(key => {
        // Prevent prototype pollution by validating property names
        if (Object.prototype.hasOwnProperty.call(filters, key)) {
          const value = filters[key];
          if (value !== undefined && value !== null && value !== '') {
            params = params.set(key, value.toString());
          }
        }
      });
    }

    return params;
  }
}
